import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import { useScenarioMode } from "../../context/ScenarioModeContext";
import { ScenarioBanner, ScenarioToggle } from "../../components/planning/ScenarioModeControls";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import RevenueSourcePanel from "../../components/planning/RevenueSourcePanel";
import RevenueSummary from "../../components/planning/revenue/RevenueSummary";
import RevenueGrid from "../../components/planning/revenue/RevenueGrid";
import PassThroughSection from "../../components/planning/revenue/PassThroughSection";
import TmDrilldownSheet from "../../components/planning/revenue/TmDrilldownSheet";
import AddSourceDialog from "../../components/planning/revenue/AddSourceDialog";
import { CATEGORY_FY_KEY, g } from "../../components/planning/revenue/format";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

export default function RevenuePage() {
  const { user, isAdmin } = useAuth();
  const { scenarioMode } = useScenarioMode();
  const canEdit = isAdmin || user?.role === "COMPANY_MANAGER";

  const [companies, setCompanies] = useState([]);
  const { companyId, setCompanyId, year, setYear } = useActiveCompany();
  const [years, setYears] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({ PROJECT: true, SERVICE: true, OTHER: true });
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [addCategory, setAddCategory] = useState(null);
  const [tmDrilldown, setTmDrilldown] = useState(null); // month number
  const [panelSourceId, setPanelSourceId] = useState(null);
  const panelSource = data?.sources.find((s) => s.id === panelSourceId) || null;

  useEffect(() => {
    Promise.all([api.get("/companies"), api.get("/customers")])
      .then(([c, cu]) => {
        setCompanies(c.data);
        setCustomers(cu.data);
        if (c.data.length > 0 && !c.data.some((co) => co.id === companyId)) setCompanyId(c.data[0].id);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    setData(null);
    api
      .get(`/companies/${companyId}/periods/years`)
      .then((res) => {
        setYears(res.data);
        const pick = pickActiveYear(res.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [companyId, year]);
  const loadRevenue = async () => {
    if (!companyId || !year) return;
    setLoading(true);
    try {
      const res = await api.get(scenarioMode ? "/scenario-outcome/revenue" : "/revenue-planning", { params: { company_id: companyId, year } });
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRevenue();
  }, [companyId, year, scenarioMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitForecast = async (sourceId, m, v) => {
    try {
      await api.put("/revenue-months/forecast", {
        revenue_source_id: sourceId,
        year,
        month: m,
        forecast_amount: v,
      });
      // Targeted optimistic update — keeps the edited input mounted (Tab/Enter flow intact).
      setData((prev) => {
        if (!prev) return prev;
        const src = prev.sources.find((s) => s.id === sourceId);
        if (!src) return prev;
        const oldVal = g(src.months, m)?.forecast_amount ?? 0;
        const newVal = v ?? 0;
        const delta = newVal - oldVal;
        const sources = prev.sources.map((s) =>
          s.id === sourceId
            ? { ...s, months: { ...s.months, [m]: { ...g(s.months, m), forecast_amount: v, value: newVal } }, fy: s.fy + delta }
            : s
        );
        const month = g(prev.months, m);
        const cat = month.categories[src.category];
        const months = {
          ...prev.months,
          [m]: {
            ...month,
            categories: {
              ...month.categories,
              [src.category]: { ...cat, forecast: cat.forecast + delta, value: cat.value + delta },
            },
            total_sales: month.total_sales + delta,
          },
        };
        const summary = {
          ...prev.summary,
          [CATEGORY_FY_KEY[src.category]]: prev.summary[CATEGORY_FY_KEY[src.category]] + delta,
          expected_total_sales: prev.summary.expected_total_sales + delta,
          forecast_remaining: prev.summary.has_closed_months
            ? prev.summary.forecast_remaining + delta
            : prev.summary.forecast_remaining,
        };
        return { ...prev, sources, months, summary };
      });
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    }
  };

  const createSource = async (form) => {
    try {
      await api.post("/revenue-sources", {
        company_id: companyId,
        category: addCategory,
        name: form.name,
        customer_id: form.customer_id || null,
        description: form.description || null,
        payment_terms_id: form.payment_terms_id || null,
        vat_rate_id: form.vat_rate_id || null,
      });
      toast.success("Revenue source created");
      setAddCategory(null);
      await loadRevenue();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const archiveSource = async (source) => {
    try {
      await api.patch(`/revenue-sources/${source.id}`, { active: !source.active });
      toast.success(source.active ? "Source deactivated" : "Source activated");
      await loadRevenue();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const visibleSources = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.sources.filter(
      (s) => !q || s.name.toLowerCase().includes(q) || (s.customer_name || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  const summary = data?.summary;

  return (
    <div data-testid="revenue-page">
      <PageHeader
        title="Revenue"
        subtitle="Monthly revenue forecast — T&M, Projects, Services and Other Revenue"
        actions={
          <div className="flex items-center gap-2">
            <ScenarioToggle />
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="rev-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {years.length > 0 && (
              <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="rev-year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`rev-year-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        }
      />
      <ScenarioBanner meta={data?.scenario_meta} />

      {companyId && years.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="rev-no-years">
          <EmptyState
            title="No financial years created"
            description="Create a financial year under Administration → Companies before planning."
            testId="rev-no-years-state"
          />
        </div>
      )}

      {year && data && summary && <RevenueSummary summary={summary} />}

      {year && (
        <>
          <div className="flex items-center gap-2 mb-4">
            <Input
              className="w-72 bg-white"
              placeholder="Search revenue sources…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="rev-search-input"
            />
          </div>

          <RevenueGrid
            data={data}
            summary={summary}
            loading={loading}
            visibleSources={visibleSources}
            expanded={expanded}
            onToggleCategory={(key) => setExpanded({ ...expanded, [key]: !expanded[key] })}
            canEdit={canEdit}
            onCommit={commitForecast}
            onAddSource={setAddCategory}
            onOpenSource={setPanelSourceId}
            onTmDrilldown={setTmDrilldown}
          />
        </>
      )}

      <PassThroughSection passThrough={data?.pass_through} />

      <TmDrilldownSheet month={tmDrilldown} year={year} data={data} onClose={() => setTmDrilldown(null)} />

      <RevenueSourcePanel
        open={!!panelSourceId}
        onClose={() => setPanelSourceId(null)}
        source={panelSource}
        periods={data?.periods || {}}
        year={year}
        canEdit={canEdit}
        isAdmin={isAdmin}
        onChanged={loadRevenue}
        onArchive={archiveSource}
      />

      <AddSourceDialog
        category={addCategory}
        customers={customers}
        onClose={() => setAddCategory(null)}
        onCreate={createSource}
      />
    </div>
  );
}
